// [ANNULATIONS] Ventes annulées / lignes retirées — qui, quand, combien. Ce que le patron regarde.
import { useEffect, useState } from "react";
import axios from "axios";
import { Ban, Loader2, UserRound, Smartphone, Trash2, ClipboardX } from "lucide-react";
import { useAuth, API } from "../App";

const TYPE = {
  commande_annulee: { label: "Commande annulée", Icon: ClipboardX },
  ligne_retiree: { label: "Ligne retirée", Icon: Ban },
  product_supprime: { label: "Article supprimé", Icon: Trash2 },
  category_supprime: { label: "Catégorie supprimée", Icon: Trash2 },
  invoice_supprime: { label: "Facture supprimée", Icon: Trash2 },
};

export default function Cancellations() {
  const { getAuthHeaders } = useAuth();
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const boutique = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutique.currencySymbol || "€";
  const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} ${devise}`;

  useEffect(() => {
    setData(null);
    axios.get(`${API}/tables/cancellations?days=${days}`, getAuthHeaders()).then((r) => setData(r.data)).catch(() => setData({ events: [], count: 0, total_amount: 0 }));
  }, [days, getAuthHeaders]);

  const byWho = (data?.events || []).reduce((acc, e) => { acc[e.who] = (acc[e.who] || 0) + (e.type === "ligne_retiree" || e.type === "commande_annulee" ? e.amount || 0 : 0); return acc; }, {});

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="cancellations-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-bold flex items-center gap-2 text-red-500"><Ban className="w-6 h-6" /> Annulations</h1>
          <p className="text-sm text-muted-foreground">Commandes annulées, articles retirés, suppressions — qui, quand, combien.</p>
        </div>
        <div className="flex rounded-lg border overflow-hidden" data-testid="cancellations-period">
          {[[1, "Aujourd'hui"], [7, "7 jours"], [30, "30 jours"]].map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 text-sm ${days === d ? "bg-red-500 text-white" : "hover:bg-muted"}`} data-testid={`period-${d}`}>{l}</button>
          ))}
        </div>
      </div>

      {!data ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="Annulations" value={data.count} testid="cancel-count" />
            <Stat label="Montant annulé" value={fmt(data.total_amount)} testid="cancel-amount" red />
            <Stat label="Par qui" value={Object.entries(byWho).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([w, a]) => `${w} ${fmt(a)}`).join(" · ") || "—"} small testid="cancel-who" />
          </div>

          {data.events.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground" data-testid="cancellations-empty">Aucune annulation sur la période. 👌</div>
          ) : (
            <div className="rounded-2xl border divide-y overflow-hidden" data-testid="cancellations-list">
              {data.events.map((e) => {
                const t = TYPE[e.type] || { label: e.type, Icon: Ban };
                const isClient = e.role === "client";
                return (
                  <div key={e.id} className="flex items-start gap-3 p-4" data-testid={`cancel-event-${e.id}`}>
                    <span className="mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444" }}><t.Icon className="w-4 h-4" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm"><span className="text-red-500">{t.label}</span>{e.table_name ? ` · ${e.table_name}` : ""}</p>
                      <p className="text-sm text-muted-foreground truncate">{e.detail}</p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        {isClient ? <Smartphone className="w-3 h-3" /> : <UserRound className="w-3 h-3" />} {e.who}{e.role && !isClient ? ` (${e.role})` : ""} · {new Date(e.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    {e.amount > 0 && <span className="font-bold text-red-500 whitespace-nowrap">− {fmt(e.amount)}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, red, small, testid }) {
  return (
    <div className="rounded-2xl border p-4" data-testid={testid}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`${small ? "text-sm" : "text-2xl"} font-bold mt-1 ${red ? "text-red-500" : ""}`}>{value}</p>
    </div>
  );
}
